import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Cookie, Settings } from "lucide-react";
import { CloseButton } from "@/components/ui/close-button";
import { useNavigate } from 'react-router';
import { recordCookieConsent } from '@/lib/analyticsConsent';

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (!localStorage.getItem('cookie-consent')) setIsVisible(true);
    } catch {
      setIsVisible(true);
    }
    const reopen = () => {
      setShowDetails(true);
      setIsVisible(true);
    };
    window.addEventListener('openCookiePreferences', reopen);
    return () => window.removeEventListener('openCookiePreferences', reopen);
  }, []);

  const notifyDismissed = () => {
    window.dispatchEvent(new CustomEvent('cookieConsentDismissed'));
  };

  const acceptAll = () => {
    recordCookieConsent('all');
    setIsVisible(false);
    notifyDismissed();
  };

  const acceptEssential = () => {
    recordCookieConsent('essential');
    setIsVisible(false);
    notifyDismissed();
  };

  const dismiss = () => {
    setIsVisible(false);
    notifyDismissed();
  };

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      // Sits ON TOP OF the bottom nav, not over it. At bottom-0/z-50 this
      // 176px sheet covered the whole 74px tab bar — on first launch of the
      // app all four tabs were invisible and untappable until it was
      // answered. z-40 keeps the nav above it if they ever do meet, and the
      // offset vars are the ones TabBar/BottomNav already maintain.
      className="fixed bottom-[calc(var(--mobile-active-bottom-nav-height,0px)+var(--mobile-safe-bottom,0px))] left-0 right-0 z-40 p-2 sm:p-4 bg-background/95 backdrop-blur-sm border-t shadow-medium"
    >
      {/* max-h keeps this a compact bottom sheet on small screens so it can
          never cover the form above it; the body scrolls internally if the
          "Customize" details are expanded. */}
      <Card className="max-w-4xl mx-auto max-h-[60vh] overflow-y-auto">
        <CardContent className="p-3 sm:p-6">
          <div className="flex items-start gap-2.5 sm:gap-4">
            <Cookie className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground mb-1 text-sm sm:text-base">We use cookies to improve your experience</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                We use cookies to analyze usage, personalize content, and provide essential security features.
                <span className="hidden sm:inline"> You can change your choice on the Cookie Policy page.</span>
              </p>

              {showDetails && (
                <div className="mb-3 p-3 bg-muted/50 rounded-lg text-xs sm:text-sm">
                  <h4 className="font-medium mb-2">Cookie Categories:</h4>
                  <ul className="space-y-1.5">
                    <li><strong>Essential:</strong> Required for basic site functionality and security</li>
                    <li><strong>Analytics:</strong> Help us understand how you use our platform</li>
                    <li><strong>Marketing:</strong> Personalize content and improve our services</li>
                  </ul>
                </div>
              )}

              {/* Primary actions sit side-by-side even on mobile (compact),
                  secondary actions on a small text row below. */}
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
                <Button onClick={acceptAll} size="sm" className="w-full sm:w-auto text-xs sm:text-sm">
                  Accept All
                </Button>
                <Button onClick={acceptEssential} variant="outline" size="sm" className="w-full sm:w-auto text-xs sm:text-sm">
                  Essential Only
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-4">
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  variant="link"
                  size="sm"
                  className="gap-1.5 text-xs p-0 h-auto"
                >
                  <Settings className="h-3.5 w-3.5" />
                  {showDetails ? 'Hide' : 'Customize'}
                </Button>
                <Button
                  onClick={() => navigate('/privacy')}
                  variant="link"
                  size="sm"
                  className="text-xs p-0 h-auto"
                >
                  Privacy Policy
                </Button>
              </div>
            </div>
            <CloseButton
              onClick={dismiss}
              label="Dismiss cookie notice"
              className="flex-shrink-0 h-8 w-8"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
